import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { Book, BookInsert } from '../types/database'

interface BookFormProps {
  book?: Book | null
  onClose: () => void
}

export function BookForm({ book, onClose }: BookFormProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState(0)
  const [displayOrder, setDisplayOrder] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (book) {
      setName(book.name)
      setDescription(book.description || '')
      setStatus(book.status)
      setDisplayOrder(book.display_order)
    }
  }, [book])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      if (book) {
        // Update existing book
        const { error: updateError } = await supabase
          .from('books')
          .update({
            name,
            description: description || null,
            status,
            display_order: displayOrder,
          })
          .eq('id', book.id)

        if (updateError) throw updateError
      } else {
        // Insert new book
        const newBook: BookInsert = {
          name,
          description: description || null,
          status,
          display_order: displayOrder,
        }

        const { error: insertError } = await supabase
          .from('books')
          .insert(newBook)

        if (insertError) throw insertError
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save book')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4 font-display">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light">
              <span className="material-symbols-outlined text-primary">
                {book ? 'edit_note' : 'add_circle'}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {book ? '교재 수정' : '새 교재 등록'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                교재 이름 <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="교재 이름을 입력하세요"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary resize-none"
                placeholder="교재에 대한 설명을 입력하세요 (선택사항)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="display_order" className="block text-sm font-medium text-gray-700 mb-2">
                  표시 순서
                </label>
                <input
                  id="display_order"
                  type="number"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                  className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">낮은 숫자가 먼저 표시됩니다</p>
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  상태
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(parseInt(e.target.value))}
                  className="form-input block w-full px-4 py-2.5 border-gray-200 rounded-lg text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value={-1}>공개 (비회원 포함)</option>
                  <option value={0}>기본 (회원만)</option>
                  <option value={1}>비공개</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-base">close</span>
              <span>취소</span>
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 border border-transparent rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                  <span>저장 중...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-base">
                    {book ? 'check' : 'add'}
                  </span>
                  <span>{book ? '수정' : '등록'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
